/*───────────────────────────────────────────────────────────────
 🌐 Project: Ring2All Platform
 🧑‍💻 Author: Rodrigo Cuadra
 🏢 Company: Ring2All LLC
 📄 File: ParkingLotsPage.tsx
 🗓️ Created: 2025-01-27
 🧩 Description:
    Main page for Parking Lots management with list view.
    Displays parking lots for call parking and retrieval.

 🔧 Notes:
    - Domain-scoped data
    - Supports pagination and search
    - Includes delete confirmation

 🔁 Last Updated: 2025-11-07 by GPT-5 Codex (ux, resilience)
────────────────────────────────────────────────────────────────*/

import { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { ParkingCircle, Edit, Copy, Trash2 } from 'lucide-react';
import DataGridSimple from '../../components/ui/DataGridSimple';
import { useI18n } from '../../hooks/useI18n';
import { useNotifications } from '../../hooks/useNotifications';
import { useContextStore } from '../../store/context';
import { SimpleTooltip } from '../../components/ui/SimpleTooltip';
import { ModulePageContainer } from '../../components/layouts';
import ConfirmationModal from '../../components/ui/ConfirmationModal';
import { deleteParkingLotById, navigateToParkingLotDuplicate } from './parkingLotsActions';
import { useParkingLotsList } from './hooks/useParkingLotsList';
import { deleteParkingLot } from './api/parkingLotsApi';
import type { ParkingLot } from './types';

export default function ParkingLotsPage() {
  const navigate = useNavigate();
  const { t } = useI18n();
  const { showSuccess, showError } = useNotifications();
  const { selectedDomain } = useContextStore();
  const [deleteModal, setDeleteModal] = useState<{ isOpen: boolean; lot: ParkingLot | null }>({
    isOpen: false,
    lot: null
  });
  const [isDeleting, setIsDeleting] = useState(false);

  const {
    data,
    loading,
    columns,
    pagination,
    searchQuery,
    sortBy,
    sortOrder,
    handleSearch,
    handleSort,
    handlePageChange,
    handleItemsPerPageChange,
    handleColumnToggle,
    refresh
  } = useParkingLotsList({
    domainId: selectedDomain?.id,
  });

  const handleCreate = useCallback(() => {
    navigate('/pbx/applications/parking/new');
  }, [navigate]);

  const handleEdit = useCallback(
    (lot: ParkingLot) => {
      navigate(`/pbx/applications/parking/${lot.uuid || lot.id}`);
    },
    [navigate]
  );

  const handleDuplicate = useCallback(
    (lot: ParkingLot) => {
      navigateToParkingLotDuplicate(navigate, lot.uuid || lot.id);
    },
    [navigate]
  );

  const handleDeleteClick = useCallback((lot: ParkingLot) => {
    setDeleteModal({ isOpen: true, lot });
  }, []);

  const handleDeleteConfirm = useCallback(async () => {
    if (!deleteModal.lot) return;

    setIsDeleting(true);
    try {
      await deleteParkingLot(String(deleteModal.lot.uuid || deleteModal.lot.id));

      showSuccess(t('parkingLots.messages.deleteSuccess'));
      setDeleteModal({ isOpen: false, lot: null });
      refresh();
    } catch (error) {
      showError(error?.message || t('parkingLots.messages.deleteError'));
    } finally {
      setIsDeleting(false);
    }
  }, [deleteModal.lot, refresh, showError, showSuccess, t]);

  const renderRowActions = useCallback(
    (lot: ParkingLot) => (
      <div className="flex items-center gap-2">
        <SimpleTooltip content={t('common.edit')} position="top">
          <button
            onClick={() => handleEdit(lot)}
            className="p-2 hover:bg-blue-100 dark:hover:bg-blue-900/30 rounded-md transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-primary/60"
          >
            <Edit className="h-4 w-4 text-blue-600 dark:text-blue-400" />
          </button>
        </SimpleTooltip>
        <SimpleTooltip content={t('common.duplicate')} position="top">
          <button
            onClick={() => handleDuplicate(lot)}
            className="p-2 hover:bg-emerald-100 dark:hover:bg-emerald-900/30 rounded-md transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-primary/60"
          >
            <Copy className="h-4 w-4 text-emerald-600 dark:text-emerald-400" />
          </button>
        </SimpleTooltip>
        <SimpleTooltip content={t('common.delete')} position="top">
          <button
            onClick={() => handleDeleteClick(lot)}
            className="p-2 hover:bg-red-100 dark:hover:bg-red-900/30 rounded-md transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-primary/60"
          >
            <Trash2 className="h-4 w-4 text-red-600 dark:text-red-400" />
          </button>
        </SimpleTooltip>
      </div>
    ),
    [handleDeleteClick, handleDuplicate, handleEdit, t]
  );

  return (
    <ModulePageContainer
      currentView="list"
      listView={
        <DataGridSimple
          data={data}
          columns={columns}
          loading={loading}
          pagination={pagination}
          searchQuery={searchQuery}
          sortBy={sortBy}
          sortOrder={sortOrder}
          onSearch={handleSearch}
          onSort={handleSort}
          onPageChange={handlePageChange}
          onItemsPerPageChange={handleItemsPerPageChange}
          onAdd={handleCreate}
          onEdit={handleEdit}
          onDuplicate={handleDuplicate}
          onDelete={handleDeleteClick}
          onColumnToggle={handleColumnToggle}
          title={t('parkingLots.title')}
          titleIcon={<ParkingCircle className="h-5 w-5 text-primary" />}
          addButtonText={t('common.add')}
          renderActions={renderRowActions}
          emptyMessage={t('parkingLots.emptyMessage')}
        />
      }
      formView={null}
    >
      <ConfirmationModal
        isOpen={deleteModal.isOpen}
        onClose={() => setDeleteModal({ isOpen: false, lot: null })}
        onConfirm={handleDeleteConfirm}
        title={t('parkingLots.deleteTitle')}
        message={t('parkingLots.messages.confirmDelete', { name: deleteModal.lot?.name || '' })}
        confirmText={t('common.delete')}
        cancelText={t('common.cancel')}
        type="danger"
        isLoading={isDeleting}
      />
    </ModulePageContainer>
  );
}

